import React, { useState } from 'react';
import { Button, Checkbox, Input, Modal, Radio, Select } from 'antd';
import MathText from './MathText';
import { figureSrc, popupInside } from './scanPdfUtils';
import {
  LANGUAGE_MODES,
  LETTERS,
  closeMath,
  COMPREHENSIVE_TYPES,
  NEW_PASSAGE,
  TYPE_LABELS,
  needsLook,
  fixProblem,
  groupProblem,
  hasAnswer,
  isImportable,
  keptPassageGroups,
  kindOf,
  parsePrintedAnswer,
  reviewStatus
} from './scanPdfMapper';

const LANGUAGE_LABELS = { en: 'English', hi: 'Hindi', mixed: 'Hindi + English' };
// Every type a teacher can turn a question into. "Other" is only what the scan calls a question it cannot place, so it
// is not offered; a question read as one can be given a real type here.
const EDITABLE_TYPES = ['mcq_single', 'mcq_multi', 'numerical', 'true_false', 'matrix_match', 'subjective'];
// The two options every true / false question has, shown (not edited) in the editor, as on the card.
const TF_OPTIONS = [['T', 'True'], ['F', 'False']];

// The kinds that have options to edit (a matching question's are its printed answer choices, if any).
const OPTION_TYPES = ['mcq_single', 'mcq_multi', 'matrix_match'];

// A matching column is edited as one row per line, "A. text"; a line without a label gets the next one.
const LEFT_LABELS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
const RIGHT_LABELS = ['p', 'q', 'r', 's', 't', 'u', 'v', 'w'];
const rowsToText = (rows) => (rows || []).map((r) => `${r.label}. ${r.text}`).join('\n');
const textToRows = (text, labels) =>
  String(text || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, i) => {
      const m = line.match(/^\(?([A-Za-z]{1,3}|[0-9]{1,2}|[ivx]{1,4})\)?[.):-]\s*(.*)$/);
      return m ? { label: m[1], text: m[2] } : { label: labels[i] || String(i + 1), text: line };
    });

// Edit text in Hindi or English, options, type, difficulty and the worked solution.
// Math is written as $...$ and previewed live, the same convention the rest of the
// test screens use.
const MIN_OPTIONS = 2;
const MAX_OPTIONS = 4;

const snapshotOf = (question) => ({
  text: question.text || '',
  solution: question.solution || '',
  question_type: question.question_type,
  difficulty: question.difficulty || '',
  options: (question.options || []).map((o) => ({ label: o.label, text: o.text })),
  left: rowsToText(question.matrix_left),
  right: rowsToText(question.matrix_right)
});

// "Comprehensive - Single correct" and the like in the Type list: the question keeps its own type and is put under a
// passage. Picking a plain type again takes it out of its passage.
const COMP = 'comp:';
const NEW_CHOICE = '__new__';

const QuestionEditor = ({ question, onSave, onCancel, types = EDITABLE_TYPES, passages = null, onPassage = () => {} }) => {
  // What the question was when the editor opened: Reset goes back to it, and Save waits for a difference from it.
  const [initial] = useState(() => snapshotOf(question));
  const [draft, setDraft] = useState(initial);
  // Passage choices, when this editor offers them: in a passage or not, which one, and the text of a new one.
  const [startPlace] = useState(() => ({ inPassage: Boolean(question.passage_id), passage: question.passage_id || '' }));
  const [place, setPlace] = useState({ ...startPlace, newText: '' });
  const placeChanged =
    Boolean(passages) && (place.inPassage !== startPlace.inPassage || (place.inPassage && place.passage !== startPlace.passage));
  const fieldsChanged = JSON.stringify(draft) !== JSON.stringify(initial);
  const changed = fieldsChanged || placeChanged;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (patch) => setDraft((prev) => ({ ...prev, ...patch }));
  const setOption = (index, text) =>
    set({ options: draft.options.map((o, i) => (i === index ? { ...o, text } : o)) });
  const compTypes = passages ? COMPREHENSIVE_TYPES.filter((t) => types.includes(t)) : [];
  const typeValue = passages && place.inPassage && compTypes.includes(draft.question_type)
    ? `${COMP}${draft.question_type}`
    : types.includes(draft.question_type) ? draft.question_type : undefined;
  const pickType = (value) => {
    if (String(value).startsWith(COMP)) {
      set({ question_type: value.slice(COMP.length) });
      setPlace((prev) => ({
        ...prev,
        inPassage: true,
        passage: prev.passage || (passages[0] && passages[0].id) || NEW_CHOICE
      }));
    } else {
      set({ question_type: value });
      setPlace((prev) => ({ ...prev, inPassage: false }));
    }
  };

  const save = async () => {
    if (placeChanged && place.inPassage && place.passage === NEW_CHOICE && !place.newText.trim()) {
      setError('Write the passage the question belongs to.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (placeChanged) {
        const isNew = place.inPassage && place.passage === NEW_CHOICE;
        const target = !place.inPassage ? null : isNew ? `${NEW_PASSAGE}${Date.now()}` : place.passage;
        onPassage(question.id, target, isNew ? place.newText : undefined);
      }
      if (!fieldsChanged) {
        onCancel(); // only the passage changed: nothing to send to the scan service
        return;
      }
      await onSave(question.id, {
        text: draft.text,
        solution: draft.solution.trim() ? draft.solution : null,
        question_type: draft.question_type,
        difficulty: draft.difficulty || null,
        // Letters follow position, which is what the test import assumes. Numeric, true/false and subjective have none.
        options: OPTION_TYPES.includes(draft.question_type)
          ? draft.options.map((o, i) => ({ label: LETTERS[i] || String(i + 1), text: o.text }))
          : draft.question_type === 'other'
            ? (question.options || []).map((o) => ({ label: o.label, text: o.text })) // kept as read
            : [],
        ...(draft.question_type === 'matrix_match'
          ? { matrix_left: textToRows(draft.left, LEFT_LABELS), matrix_right: textToRows(draft.right, RIGHT_LABELS) }
          : {})
      });
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="scan-editor">
      <div className="form-group">
        <label>Question text (Hindi or English, math as $...$)</label>
        <Input.TextArea
          rows={4}
          lang="hi"
          value={draft.text}
          onChange={(e) => set({ text: e.target.value })}
          data-testid="edit-text"
        />
      </div>
      <div className="scan-editor-row">
        <div className="form-group">
          <label>Type</label>
          <Select
            getPopupContainer={popupInside}
            value={typeValue}
            placeholder="Choose a type"
            onChange={pickType}
            style={{ width: '100%' }}
            data-testid="edit-type"
          >
            {types.map((t) => (
              <Select.Option key={t} value={t}>{TYPE_LABELS[t]}</Select.Option>
            ))}
            {compTypes.map((t) => (
              <Select.Option key={`${COMP}${t}`} value={`${COMP}${t}`}>Comprehensive - {TYPE_LABELS[t]}</Select.Option>
            ))}
          </Select>
        </div>
        <div className="form-group">
          <label>Difficulty</label>
          <Select
            getPopupContainer={popupInside}
            value={draft.difficulty}
            onChange={(value) => set({ difficulty: value })}
            style={{ width: '100%' }}
          >
            <Select.Option value="">Not set</Select.Option>
            <Select.Option value="easy">Easy</Select.Option>
            <Select.Option value="medium">Medium</Select.Option>
            <Select.Option value="hard">Hard</Select.Option>
          </Select>
        </div>
      </div>
      {passages && place.inPassage && (
        <div className="form-group" data-testid="edit-passage-choice">
          <label>Passage<span className="mandatory-star">*</span></label>
          <Select
            getPopupContainer={popupInside}
            value={place.passage || undefined}
            placeholder="Choose the passage"
            onChange={(value) => setPlace((prev) => ({ ...prev, passage: value }))}
            style={{ width: '100%' }}
            data-testid="edit-passage-select"
          >
            {passages.map((p) => (
              <Select.Option key={p.id} value={p.id}>{p.label}</Select.Option>
            ))}
            <Select.Option value={NEW_CHOICE}>+ New passage</Select.Option>
          </Select>
          {place.passage === NEW_CHOICE && (
            <Input.TextArea
              rows={4}
              lang="hi"
              placeholder="Write the passage (math as $...$)"
              value={place.newText}
              onChange={(e) => setPlace((prev) => ({ ...prev, newText: e.target.value }))}
              style={{ marginTop: 8 }}
              data-testid="edit-new-passage-text"
            />
          )}
        </div>
      )}
      {draft.question_type === 'matrix_match' && (
        <div className="scan-editor-row">
          <div className="form-group">
            <label>Column I (one row per line, e.g. A. Water)</label>
            <Input.TextArea
              autoSize={{ minRows: 3, maxRows: 8 }}
              lang="hi"
              value={draft.left}
              onChange={(e) => set({ left: e.target.value })}
              data-testid="edit-column-1"
            />
          </div>
          <div className="form-group">
            <label>Column II (one row per line, e.g. p. Bent)</label>
            <Input.TextArea
              autoSize={{ minRows: 3, maxRows: 8 }}
              lang="hi"
              value={draft.right}
              onChange={(e) => set({ right: e.target.value })}
              data-testid="edit-column-2"
            />
          </div>
        </div>
      )}
      {OPTION_TYPES.includes(draft.question_type) && (
        <div className="form-group">
          <label>{draft.question_type === 'matrix_match' ? 'Answer choices (optional)' : 'Options'}</label>
          {draft.options.map((o, i) => (
            <div className="scan-editor-option" key={i}>
              <span className="option-letter">{LETTERS[i] || i + 1}</span>
              <Input.TextArea
                autoSize={{ minRows: 1, maxRows: 4 }}
                lang="hi"
                value={o.text}
                onChange={(e) => setOption(i, e.target.value)}
                data-testid={`edit-option-${i}`}
              />
              {draft.options.length > MIN_OPTIONS && (
              <Button
                size="small"
                className="radius-100"
                data-testid={`remove-option-${i}`}
                onClick={() => set({ options: draft.options.filter((_, k) => k !== i) })}
              >
                Remove
              </Button>
              )}
            </div>
          ))}
          {draft.options.length < MAX_OPTIONS && (
            <Button
              size="small"
              className="radius-100"
              data-testid="add-option"
              onClick={() => set({ options: [...draft.options, { label: '', text: '' }] })}
            >
              + Add option
            </Button>
          )}
        </div>
      )}
      {draft.question_type === 'true_false' && (
        // A true / false question always has these two; the right one is picked on the card after saving.
        <div className="form-group" data-testid="edit-tf-options">
          <label>Options</label>
          {TF_OPTIONS.map(([letter, label]) => (
            <div className="scan-editor-option scan-editor-option-fixed" key={letter}>
              <span className="option-letter">{letter}</span>
              <span className="scan-editor-fixed-text">{label}</span>
            </div>
          ))}
        </div>
      )}
      <div className="form-group">
        <label>Solution (optional)</label>
        <Input.TextArea
          rows={2}
          lang="hi"
          value={draft.solution}
          onChange={(e) => set({ solution: e.target.value })}
        />
      </div>
      <div className="scan-preview">
        <div className="scan-preview-label">Preview</div>
        <div className="mcq-question"><MathText>{draft.text}</MathText></div>
        {OPTION_TYPES.includes(draft.question_type) && draft.options.map((o, i) => (
          <div key={i} className="option-item">
            <div className="option-letter">{LETTERS[i] || i + 1}</div>
            <div className="option-text"><MathText>{o.text}</MathText></div>
          </div>
        ))}
        {draft.question_type === 'true_false' && TF_OPTIONS.map(([letter, label]) => (
          <div key={letter} className="option-item">
            <div className="option-letter">{letter}</div>
            <div className="option-text">{label}</div>
          </div>
        ))}
      </div>
      {error && <div className="error-message"><span>{error}</span></div>}
      <div className="scan-editor-actions">
        <Button onClick={onCancel} className="radius-100" disabled={saving}>CANCEL</Button>
        <Button onClick={() => { setDraft(initial); setPlace({ ...startPlace, newText: '' }); setError(''); }} className="radius-100" disabled={saving || !changed} data-testid="edit-reset">
          RESET
        </Button>
        <Button
          loading={saving}
          disabled={!changed}
          onClick={save}
          className="purpleBtnWrap radius-100 purpleButtonHoverEffect"
          data-testid="edit-save"
        >
          SAVE
        </Button>
      </div>
    </div>
  );
};

// A written answer solved by the scan service ends its solution with a "Final answer: ..." paragraph (in Hindi,
// "अंतिम उत्तर: ..."). It is shown on its own line with the label in bold, so it stands apart from the working.
const FINAL_ANSWER = /\n\s*\n(Final answer|अंतिम उत्तर):\s*([\s\S]*)$/;
const SolutionText = ({ text }) => {
  const value = String(text ?? '');
  const match = value.match(FINAL_ANSWER);
  if (!match) return <MathText>{value}</MathText>;
  return (
    <>
      <MathText>{value.slice(0, match.index)}</MathText>
      <div className="scan-final-answer" data-testid="final-answer">
        <strong>{match[1]}:</strong> <MathText>{closeMath(match[2])}</MathText>
      </div>
    </>
  );
};

// A matching question's two columns, side by side, row for row.
const MatchingTable = ({ question }) => {
  const left = question.matrix_left || [];
  const right = question.matrix_right || [];
  const rows = Math.max(left.length, right.length);
  if (!rows) return null;
  return (
    <table className="scan-matrix" data-testid={`matrix-${question.id}`}>
      <thead>
        <tr><th colSpan={2}>Column I</th><th colSpan={2}>Column II</th></tr>
      </thead>
      <tbody>
        {Array.from({ length: rows }, (_, i) => (
          <tr key={i}>
            <td className="scan-matrix-label">{left[i] ? `(${left[i].label})` : ''}</td>
            <td>{left[i] && <MathText>{left[i].text}</MathText>}</td>
            <td className="scan-matrix-label">{right[i] ? `(${right[i].label})` : ''}</td>
            <td>{right[i] && <MathText>{right[i].text}</MathText>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// Whose answer this is now. The AI's answer (or the paper's) stays theirs only until the teacher takes it over: by
// saving an edit of the question, or by picking a different answer on the card. Picking it again gives it back.
const sameAnswer = (a, b) =>
  Array.isArray(a) || Array.isArray(b)
    ? [...(a || [])].sort().join() === [...(b || [])].sort().join()
    : String(a ?? '').trim() === String(b ?? '').trim();
const answerFrom = (q, picked) => {
  if (!q || q.edited || (q.answer_source !== 'generated' && q.answer_source !== 'printed')) return null;
  return sameAnswer(picked, parsePrintedAnswer(q)) ? q.answer_source : null;
};
const solutionLabel = (q, language) =>
  `Solution${language ? ` (${language})` : ''}${q.solution_source === 'generated' ? ', AI-generated' : ''}:`;

const AnswerPicker = ({ question, answer, onAnswer, missing }) => {
  const kind = kindOf(question);
  if (kind === 'subjective') {
    // Optional. It goes into the test as the solution's last paragraph ("Final answer: ...").
    return (
      <div className="scan-answer-row scan-written-answer">
        <div className="answer-label">Answer:</div>
        <Input.TextArea
          autoSize={{ minRows: 1, maxRows: 6 }}
          placeholder="Enter the answer (optional)"
          value={answer ?? ''}
          lang="hi"
          onChange={(e) => onAnswer(question.id, e.target.value)}
          data-testid={`answer-written-${question.id}`}
        />
      </div>
    );
  }
  if (kind === 'matrix') {
    // A matching question with no printed choices: the right matching is typed, and goes in as its one option.
    return (
      <div className="scan-answer-row scan-written-answer">
        <div className="answer-label">Right matching:</div>
        <Input
          placeholder="e.g. A-q, B-p, C-r"
          value={answer ?? ''}
          onChange={(e) => onAnswer(question.id, e.target.value)}
          data-testid={`answer-matrix-${question.id}`}
        />
      </div>
    );
  }
  if (kind === 'numeric') {
    return (
      <div className="scan-answer-row">
        <div className="answer-label">Right answer (number):</div>
        <Input
          style={{ width: 160 }}
          inputMode="decimal"
          placeholder="e.g. 4"
          value={answer ?? ''}
          onChange={(e) => onAnswer(question.id, e.target.value)}
          data-testid={`answer-numeric-${question.id}`}
        />
      </div>
    );
  }
  // While an answer is still missing the red line below says what to do, so this one stays quiet.
  if (missing) return null;
  return (
    <div className="scan-answer-row">
      <div className="answer-label">
        {kind === 'multi' ? 'Tick every right option above' : 'Click the right option above'}
      </div>
    </div>
  );
};

// The passage that several questions share. It is shown once, above them, and goes to the test with them as one
// comprehension question.
const PassageBlock = ({ passage, members, keep, testKind, onToggleGroup, onEditPassage, onRemovePassage = () => {} }) => {
  // Removing the passage keeps its questions, each as a separate question, so it only asks to be sure.
  const confirmRemove = () =>
    Modal.confirm({
      title: 'Remove this passage?',
      content: `Its ${members.length} question${members.length === 1 ? '' : 's'} will stay, each as a separate question.`,
      okText: 'Remove passage',
      okType: 'danger',
      cancelText: 'Cancel',
      centered: true,
      onOk: () => onRemovePassage(members[0].passage_id)
    });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // A written question under a passage goes to a hybrid test on its own, not as a part of the passage.
  const importable = members.filter((m) => isImportable(m, testKind) && (testKind === 'subjective' || m.question_type !== 'subjective'));
  const keptCount = importable.filter((m) => keep[m.id]).length;
  const problem = keptCount > 0 ? groupProblem(importable.filter((m) => keep[m.id])) : '';
  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await onEditPassage(members[0].passage_id, draft);
      setEditing(false);
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };
  return (
    <div className="mcq-card scan-passage" data-testid={`passage-${members[0].passage_id}`}>
      <div className="mcq-header">
        <div className="mcq-header-left">
          <Checkbox
            checked={importable.length > 0 && keptCount === importable.length}
            indeterminate={keptCount > 0 && keptCount < importable.length}
            onChange={(e) => onToggleGroup(members[0].passage_id, e.target.checked)}
            className="question-checkbox"
            data-testid={`keep-passage-${members[0].passage_id}`}
          >
            <span className="select-all-label">Keep all {importable.length} questions of this passage</span>
          </Checkbox>
        </div>
        <div className="mcq-header-right">
          <div className="question-badges"><span className="question-topic">Passage</span></div>
          {!editing && (
            <Button
              size="small"
              className="radius-100"
              onClick={() => { setDraft(passage || ''); setEditing(true); }}
              data-testid={`edit-passage-${members[0].passage_id}`}
            >
              Edit passage
            </Button>
          )}
          {!editing && (
            <Button
              size="small"
              className="radius-100 scan-remove-passage"
              onClick={confirmRemove}
              data-testid={`remove-passage-${members[0].passage_id}`}
            >
              Remove passage
            </Button>
          )}
        </div>
      </div>
      {editing ? (
        <div className="scan-editor">
          <Input.TextArea rows={5} lang="hi" value={draft} onChange={(e) => setDraft(e.target.value)} data-testid="edit-passage-text" />
          {error && <div className="error-message"><span>{error}</span></div>}
          <div className="scan-editor-actions">
            <Button className="radius-100" onClick={() => setEditing(false)} disabled={saving}>CANCEL</Button>
            <Button
              loading={saving}
              disabled={draft === (passage || '')}
              onClick={save}
              className="purpleBtnWrap radius-100 purpleButtonHoverEffect"
              data-testid="save-passage"
            >
              SAVE
            </Button>
          </div>
        </div>
      ) : (
        <div className="mcq-body">
          <div className="mcq-question scan-passage-text"><MathText>{passage || ''}</MathText></div>
        </div>
      )}
      {problem && <div className="scan-note warn" data-testid="passage-problem">{problem}</div>}
    </div>
  );
};

const ScanReview = ({
  questions,
  keep,
  answers,
  onToggleKeep,
  onKeepAll,
  onAnswer,
  onSaveEdit,
  focusId,
  modes = {},
  onMode = () => {},
  onSplit = () => {},
  testKind = 'objective', // 'objective', 'subjective' or 'hybrid' (both kinds): which questions can be kept
  onToggleGroup = () => {},
  onEditPassage = async () => {},
  subjectLabel = null, // set for a mock test with several subjects, to tag each card with its subject
  // Does the question belong in the subject and chapter the teacher chose? (a warning only, never a block)
  checks = {},
  filing = {},
  aiFiled = {}, // questions filed under the AI's chapter (the default where it disagrees with the teacher)
  movedTo = {}, // questions filed under another subject of the test, the AI's choice: id -> subject name
  reviewed = {},
  subjectName = (value) => value,
  chapterChoices = () => [],
  onOverride = () => {},
  onReviewed = () => {},
  // The chapter the scan suggests for a question, if the test can take it, and what to do when the teacher keeps it.
  aiChoiceFor = () => null,
  onKeepAiChoice = () => {},
  kindChoice = null, // for a paper holding both kinds: the choice of which to keep, shown in the card above the list
  objectiveOnly = false, // a JEE or NEET paper: its questions cannot be made subjective
  // The teacher's passage choices: the passages a question can be put under, putting it under one (or taking it out),
  // and removing a passage while keeping its questions.
  passages = [],
  onSetPassage = () => {},
  onRemovePassage = () => {}
}) => {
  const editTypes = objectiveOnly ? EDITABLE_TYPES.filter((t) => t !== 'subjective') : EDITABLE_TYPES;
  const [editingId, setEditingId] = useState(null);
  const status = reviewStatus(questions, keep, answers, testKind);
  const allKept = status.importable > 0 && status.kept === status.importable;

  const pickOption = (q, letter) => {
    if (kindOf(q) === 'multi') {
      const current = Array.isArray(answers[q.id]) ? answers[q.id] : [];
      onAnswer(q.id, current.includes(letter) ? current.filter((l) => l !== letter) : [...current, letter]);
    } else {
      onAnswer(q.id, letter);
    }
  };

  return (
    <div className="scan-review">
      {/* One card above the list: how joined questions work, which kind of question to keep, and Keep all. */}
      <div className="scan-review-top" data-testid="review-top">
        <div className="scan-note">
          A question printed in both Hindi and English is joined into one card. If two were joined by mistake, press Split.
        </div>
        {kindChoice}
        <div className="regeneration-bar">
          <div className="regeneration-bar-left">
            <Checkbox
              checked={allKept}
              indeterminate={status.kept > 0 && !allKept}
              disabled={status.importable === 0}
              onChange={(e) => onKeepAll(e.target.checked)}
              className="question-checkbox"
            >
              <span className="select-all-label">Keep all ({status.importable})</span>
            </Checkbox>
            {status.needAnswer > 0 && (
              <span className="scan-pill warn" data-testid="need-answer">
                {status.needAnswer} need a right answer
              </span>
            )}
            {status.needFix > 0 && (
              <span className="scan-pill bad" data-testid="need-fix">{status.needFix} need fixing</span>
            )}
          </div>
        </div>
      </div>

      <div className="mcqs-container">
        {questions.map((q, index) => {
          const importable = isImportable(q, testKind);
          const kept = importable && !!keep[q.id];
          const startsPassage =
            Boolean(q.passage_id) && importable && (testKind === 'subjective' || q.question_type !== 'subjective') &&
            (index === 0 || questions[index - 1].passage_id !== q.passage_id);
          const answer = answers[q.id];
          const problem = kept ? fixProblem(q) : '';
          const missing = kept && !hasAnswer(q, answer);
          const kind = kindOf(q);
          const twin = q.secondary; // the Hindi version joined onto this English question
          // 'generated' or 'printed' while the answer in use is still the AI's or the paper's; null once it is the teacher's.
          const answerSource = answerFrom(q, answer) || (twin && !q.edited ? answerFrom(twin, answer) : null);
          const editingMember = editingId === q.id ? q : (twin && editingId === twin.id ? twin : null);
          const figures = (q.figures || []).length ? q.figures : ((twin && twin.figures) || []);
          const optionFigures = (q.options || []).some((o) => (o.figures || []).length > 0);
          const extraFigures = figures.length > 1;
          const selected = (letter) =>
            Array.isArray(answer) ? answer.includes(letter) : answer === letter;
          return (
            <React.Fragment key={q.id}>
            {startsPassage && (
              <PassageBlock
                passage={q.passage}
                members={questions.filter((m) => m.passage_id === q.passage_id)}
                keep={keep}
                testKind={testKind}
                onToggleGroup={onToggleGroup}
                onEditPassage={onEditPassage}
                onRemovePassage={onRemovePassage}
              />
            )}
            <div
              id={`scan-q-${q.id}`}
              className={`mcq-card scan-card ${kept ? 'mcq-card-selected' : ''} ${!importable ? 'scan-card-off' : ''} ${(missing || problem) ? 'scan-card-warn' : ''} ${focusId === q.id ? 'scan-card-focus' : ''}`}
            >
              <div className="mcq-header scan-card-head">
                <div className="scan-head-top">
                  <div className="mcq-header-left">
                    <Checkbox
                      checked={kept}
                      disabled={!importable}
                      onChange={() => onToggleKeep(q.id)}
                      className="question-checkbox"
                      data-testid={`keep-${q.id}`}
                    />
                    <div className="question-number scan-question-number">Question #{index + 1}</div>
                    {q.number && String(q.number) !== String(index + 1) && (
                      <span className="scan-paper-no" title="The number printed in the paper">Paper Q{q.number}</span>
                    )}
                  </div>
                  <div className="scan-head-actions">
                  {importable && !editingMember && q.passage_id && testKind !== 'subjective' && (
                    // The question leaves its passage and goes to the test on its own, as its own type.
                    <Button
                      size="small"
                      className="radius-100"
                      onClick={() => onSetPassage(q.id, null)}
                      data-testid={`unlink-${q.id}`}
                    >
                      Take out of passage
                    </Button>
                  )}
                  {importable && !editingMember && (twin ? (
                    <>
                      <Button size="small" className="radius-100" onClick={() => setEditingId(q.id)} data-testid={`edit-${q.id}`}>
                        Edit English
                      </Button>
                      <Button size="small" className="radius-100" onClick={() => setEditingId(twin.id)} data-testid={`edit-${twin.id}`}>
                        Edit Hindi
                      </Button>
                      <Button size="small" className="radius-100" onClick={() => onSplit(q.id)} data-testid={`split-${q.id}`}>
                        Split
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="small"
                      className="radius-100"
                      onClick={() => setEditingId(q.id)}
                      data-testid={`edit-${q.id}`}
                    >
                      Edit
                    </Button>
                  ))}
                  </div>
                </div>
                {/* One line says what the question is; a quieter one where it is filed. Notes below say what to check. */}
                <div className="scan-tags">
                  <div className="scan-tag-row">
                    <span className="scan-tag scan-tag-type">{TYPE_LABELS[q.question_type] || q.question_type}</span>
                    {(q.difficulty || (twin && twin.difficulty)) && (
                      <span className={`scan-tag scan-diff scan-diff-${q.difficulty || twin.difficulty}`}>
                        {q.difficulty || twin.difficulty}
                      </span>
                    )}
                    {subjectLabel && q.subjectValue && (
                      <span className="scan-tag scan-subject-chip" data-testid={`subject-${q.id}`}>{movedTo[q.id] || subjectLabel(q.subjectValue)}</span>
                    )}
                    {q.passage_id && importable && <span className="scan-tag scan-tag-soft">Passage question</span>}
                    {q.edited && <span className="scan-tag scan-tag-ok">edited</span>}
                    {importable && needsLook(checks[q.id]) && !reviewed[q.id] && (
                      <span className="scan-tag scan-tag-warn" data-testid={`look-${q.id}`}>check subject / chapter</span>
                    )}
                    <span className="scan-meta-text">
                      {twin ? 'English + Hindi' : (LANGUAGE_LABELS[q.language] || q.language)}
                      <span className="scan-meta-dot">·</span>
                      {q.page_start === q.page_end ? `Page ${q.page_start}` : `Pages ${q.page_start}-${q.page_end}`}
                    </span>
                  </div>
                  {((importable && filing[q.id]) || q.topic) && (
                    <div className="scan-filed">
                      {importable && filing[q.id] && (
                        <span data-testid={`chapter-${q.id}`}>
                          <span className="scan-meta-key">Chapter:</span> {filing[q.id]}
                          {aiFiled[q.id] && (
                            <span className="scan-tag scan-tag-ai" data-testid={`ai-chapter-${q.id}`} title="Not on your chapter list: the AI named this chapter. You can add it to your syllabus on the next screen.">
                              AI chapter
                            </span>
                          )}
                        </span>
                      )}
                      {importable && filing[q.id] && q.topic && <span className="scan-meta-dot">·</span>}
                      {q.topic && (
                        <span>
                          <span className="scan-meta-key">Topic:</span> {q.topic}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {!importable && (
                <div className="scan-note warn">
                  {testKind === 'subjective'
                    ? 'Only subjective questions can go in a subjective test, so this one is left out.'
                    : `${TYPE_LABELS[q.question_type] || 'This kind of'} questions cannot be added to ${testKind === 'hybrid' ? 'this' : 'an objective'} test, so this one is left out.`}
                </div>
              )}
              {q.needs_review && (q.review_reasons || []).length > 0 && (
                <div className="scan-note warn">{q.review_reasons.join('; ')}</div>
              )}
              {/* Once the teacher has decided (either button, or a chapter picked below), the note has done its job. */}
              {importable && needsLook(checks[q.id]) && !reviewed[q.id] && (
                <div className="scan-note scan-check warn" data-testid={`check-${q.id}`}>
                  <div>
                    {checks[q.id].status === 'subject'
                      ? `Looks like ${checks[q.id].suggestedSubject}${checks[q.id].suggestedChapter ? ` / ${checks[q.id].suggestedChapter}` : ''}, but this PDF is tagged ${subjectName(q.subjectValue)}. ${movedTo[q.id]
                        ? `Filed under ${movedTo[q.id]}, the AI's choice, unless you keep yours.`
                        : `${checks[q.id].suggestedSubject} is not a subject of this test, so it stays under ${subjectName(q.subjectValue)}.`}`
                      : checks[q.id].status === 'chapter' && checks[q.id].offList
                        ? `No chapter on your list fits. ${aiFiled[q.id] ? 'Filed under' : 'The AI suggests'} ${checks[q.id].suggestedChapter}, the chapter the AI names; it can be added to your syllabus on the next screen.`
                        : checks[q.id].status === 'chapter'
                        ? `Looks like ${checks[q.id].suggestedChapter}, which is not one of the chapters you chose.${aiFiled[q.id] ? " Filed under it, the AI's choice, unless you keep yours." : ''}`
                        : 'Could not match this to any chapter. Check the subject and chapter.'}
                  </div>
                  <div className="scan-check-row">
                    {chapterChoices(q).length > 0 && (
                      <>
                        <span>Filed under</span>
                        <Select
                          getPopupContainer={popupInside}
                          size="small"
                          value={filing[q.id] || undefined}
                          onChange={(value) => onOverride(q.id, value)}
                          style={{ minWidth: 200 }}
                          data-testid={`file-under-${q.id}`}
                        >
                          {chapterChoices(q).map((name) => (
                            <Select.Option key={name} value={name}>{name}</Select.Option>
                          ))}
                        </Select>
                      </>
                    )}
                    <Button size="small" className="radius-100" onClick={() => onReviewed(q.id)} data-testid={`keep-mine-${q.id}`}>
                      Keep my choice
                    </Button>
                    {aiChoiceFor(q) && (
                      <Button
                        size="small"
                        className="radius-100 scan-keep-ai"
                        title={`Use the AI's choice: ${aiChoiceFor(q)}`}
                        onClick={() => onKeepAiChoice(q.id, aiChoiceFor(q))}
                        data-testid={`keep-ai-${q.id}`}
                      >
                        Keep AI choice
                      </Button>
                    )}
                  </div>
                </div>
              )}
              {editingMember ? (
                <QuestionEditor
                  types={editTypes}
                  question={editingMember}
                  // A passage belongs to the whole card, so it is chosen when editing the card's own question, and only
                  // for an objective test (a subjective test takes its questions one by one).
                  passages={editingMember === q && testKind !== 'subjective' ? passages : null}
                  onPassage={(id, passageId, text) => onSetPassage(id, passageId, text)}
                  onCancel={() => setEditingId(null)}
                  onSave={async (id, body) => {
                    await onSaveEdit(id, body);
                    setEditingId(null);
                  }}
                />
              ) : (
                <div className="mcq-body">
                  {twin ? (
                    <>
                      <div className="scan-lang"><span className="scan-lang-tag">English</span><div className="mcq-question"><MathText>{q.text}</MathText></div></div>
                      <div className="scan-lang"><span className="scan-lang-tag">Hindi</span><div className="mcq-question"><MathText>{twin.text}</MathText></div></div>
                    </>
                  ) : (
                    <div className="mcq-question"><MathText>{q.text}</MathText></div>
                  )}
                  {q.question_type === 'matrix_match' && <MatchingTable question={q} />}
                  {figures.map((f) => (
                    <div className="question-diagram" key={f.key}>
                      <img
                        src={figureSrc(f)}
                        alt={`Diagram for question ${q.number || index + 1}`}
                        loading="lazy"
                        onError={(e) => e.currentTarget.parentElement.classList.add('failed')}
                      />
                      <span className="diagram-fallback">Diagram could not be loaded</span>
                    </div>
                  ))}
                  {kind !== 'numeric' && (q.options || []).length > 0 && (
                    <div className="mcq-options">
                      {q.options.map((o, i) => {
                        const letter = LETTERS[i] || String(i + 1);
                        const isRight = selected(letter);
                        return (
                          <div
                            key={`${letter}-${i}`}
                            className={`option-item ${importable ? 'scan-option' : ''} ${isRight ? 'correct' : ''}`}
                            role={importable ? 'button' : undefined}
                            tabIndex={importable ? 0 : undefined}
                            aria-pressed={importable ? isRight : undefined}
                            data-testid={`option-${q.id}-${letter}`}
                            onClick={importable ? () => pickOption(q, letter) : undefined}
                            onKeyDown={importable ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                pickOption(q, letter);
                              }
                            } : undefined}
                          >
                            <div className={`option-letter ${isRight ? 'correct' : ''}`}>{letter}</div>
                            <div className="option-text">
                              <MathText>{o.text}</MathText>
                              {twin && twin.options && twin.options[i] && twin.options[i].text !== o.text && (
                                <div className="scan-option-alt"><MathText>{twin.options[i].text}</MathText></div>
                              )}
                              {(o.figures || []).map((f) => (
                                <img key={f.key} className="scan-option-figure" src={figureSrc(f)} alt={`Option ${letter}`} />
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {kind === 'tf' && (
                    <div className="mcq-options">
                      {[['true', 'True'], ['false', 'False']].map(([value, label]) => {
                        const isRight = selected(value);
                        return (
                          <div
                            key={value}
                            className={`option-item ${importable ? 'scan-option' : ''} ${isRight ? 'correct' : ''}`}
                            role={importable ? 'button' : undefined}
                            tabIndex={importable ? 0 : undefined}
                            aria-pressed={importable ? isRight : undefined}
                            data-testid={`option-${q.id}-${value}`}
                            onClick={importable ? () => onAnswer(q.id, value) : undefined}
                            onKeyDown={importable ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onAnswer(q.id, value);
                              }
                            } : undefined}
                          >
                            <div className={`option-letter ${isRight ? 'correct' : ''}`}>{label.charAt(0)}</div>
                            <div className="option-text">{label}</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {(optionFigures || extraFigures) && (
                    <div className="scan-note warn">
                      {optionFigures && 'Pictures inside options are shown here but are not saved to the test. '}
                      {extraFigures && 'Only the first diagram is saved to the test.'}
                    </div>
                  )}
                  {kept && <AnswerPicker question={q} answer={answer} onAnswer={onAnswer} missing={missing} />}
                  {kept && twin && (
                    <div className="scan-mode-row">
                      <span className="answer-label">In the test:</span>
                      <Radio.Group
                        value={modes[q.id] || 'both'}
                        onChange={(e) => onMode(q.id, e.target.value)}
                        data-testid={`mode-${q.id}`}
                      >
                        {LANGUAGE_MODES.map((m) => (
                          <Radio key={m.value} value={m.value}>{m.label}</Radio>
                        ))}
                      </Radio.Group>
                    </div>
                  )}
                  {kept && missing && (
                    <div className="scan-missing" data-testid={`missing-${q.id}`}>Pick the right answer to continue.</div>
                  )}
                  {problem && <div className="scan-missing">{problem}. Use Edit to fix it.</div>}
                  {answerSource === 'generated' && (
                    <div
                      className="scan-answer-source ai"
                      title="Worked out by AI, not printed in the paper"
                      data-testid={`ai-${q.id}`}
                    >
                      AI-generated answer · check it before use
                    </div>
                  )}
                  {answerSource === 'printed' && (
                    <div className="scan-answer-source paper" data-testid={`printed-${q.id}`}>Answer printed in the paper</div>
                  )}
                  {q.solution && (
                    <div className="mcq-answer">
                      <div className="answer-label">{solutionLabel(q, twin ? 'English' : '')}</div>
                      <div className="answer-explanation"><SolutionText text={q.solution} /></div>
                    </div>
                  )}
                  {twin && twin.solution && (
                    <div className="mcq-answer">
                      <div className="answer-label">{solutionLabel(twin, 'Hindi')}</div>
                      <div className="answer-explanation"><SolutionText text={twin.solution} /></div>
                    </div>
                  )}
                </div>
              )}
            </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default ScanReview;
